import { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
    MapPin, 
    Truck,
    MessageSquare,
    Bell,
    AlertTriangle,
    CheckCircle,
    Clock,
    Navigation,
    RefreshCw,
    Share2,
    Copy,
    Phone,
    Camera
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const truckIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

export default function GPSTrackingPage() {
    const [trucks, setTrucks] = useState([]);
    const [loads, setLoads] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTruck, setSelectedTruck] = useState(null);
    const [showSMSDialog, setShowSMSDialog] = useState(false);
    const [smsLink, setSmsLink] = useState(null);
    const [showShareDialog, setShowShareDialog] = useState(false);
    const [selectedLoad, setSelectedLoad] = useState(null);
    const [shareUrl, setShareUrl] = useState('');

    const fetchData = useCallback(async () => {
        try {
            const [trucksRes, loadsRes, alertsRes] = await Promise.all([
                fetch(`${API_URL}/api/trucks`, { credentials: 'include' }),
                fetch(`${API_URL}/api/loads?status=in_transit`, { credentials: 'include' }),
                fetch(`${API_URL}/api/tracking/alerts?acknowledged=false`, { credentials: 'include' })
            ]);

            if (trucksRes.ok) setTrucks(await trucksRes.json());
            if (loadsRes.ok) setLoads(await loadsRes.json());
            if (alertsRes.ok) setAlerts(await alertsRes.json());
        } catch (error) {
            console.error('Failed to fetch:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        // Poll every 30 seconds
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const generateTrackingLink = async (truck) => {
        try {
            const load = loads.find(l => l.assigned_truck_id === truck.truck_id);
            const url = `${API_URL}/api/tracking/generate-link/${truck.truck_id}${load ? `?load_id=${load.load_id}` : ''}`;
            
            const response = await fetch(url, { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                setSmsLink(data);
                setSelectedTruck(truck);
                setShowSMSDialog(true);
            }
        } catch (error) {
            console.error('Failed to generate link:', error);
            toast.error('Failed to generate tracking link');
        }
    };

    const createShareLink = async (load) => {
        try {
            const response = await fetch(`${API_URL}/api/tracking/share/${load.load_id}`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setShareUrl(data.share_url);
                setSelectedLoad(load);
                setShowShareDialog(true);
            }
        } catch (error) {
            console.error('Failed to create share:', error);
            toast.error('Failed to create share link');
        }
    };

    const acknowledgeAlert = async (alertId) => {
        try {
            await fetch(`${API_URL}/api/tracking/alerts/${alertId}/acknowledge`, {
                method: 'PATCH',
                credentials: 'include'
            });
            setAlerts(alerts.filter(a => a.alert_id !== alertId));
            toast.success('Alert acknowledged');
        } catch (error) {
            console.error('Failed to acknowledge:', error);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    const getAlertIcon = (type) => {
        switch (type) {
            case 'stationary': return <Clock className="w-4 h-4" />;
            case 'detention': return <AlertTriangle className="w-4 h-4" />;
            case 'arrival': return <Camera className="w-4 h-4" />;
            default: return <Bell className="w-4 h-4" />;
        }
    };

    const getAlertColor = (type) => {
        switch (type) {
            case 'stationary': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
            case 'detention': return 'bg-red-500/20 text-red-400 border-red-500/30';
            case 'arrival': return 'bg-green-500/20 text-green-400 border-green-500/30';
            default: return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
        }
    };

    const trucksWithLocation = trucks.filter(t => t.current_location);

    return (
        <div className="space-y-6 animate-fade-in" data-testid="gps-tracking-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase">
                        GPS Tracking
                    </h1>
                    <p className="text-zinc-500 text-sm mt-1">
                        Text-to-Track SMS & ELD Integration
                        <span className="ml-2 text-orange-500 text-xs">(MOCK)</span>
                    </p>
                </div>
                <Button 
                    onClick={fetchData}
                    disabled={loading}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* Alerts Banner */}
            {alerts.length > 0 && (
                <div className="space-y-2">
                    {alerts.slice(0, 5).map((alert) => (
                        <div 
                            key={alert.alert_id}
                            className={`p-3 rounded-sm border flex items-center justify-between ${getAlertColor(alert.alert_type)}`}
                        >
                            <div className="flex items-center gap-3">
                                {getAlertIcon(alert.alert_type)}
                                <div>
                                    <p className="font-semibold uppercase text-sm">{alert.alert_type} Alert</p>
                                    <p className="text-sm opacity-80">{alert.message}</p>
                                </div>
                            </div>
                            <Button 
                                size="sm" 
                                variant="outline"
                                className="border-current"
                                onClick={() => acknowledgeAlert(alert.alert_id)}
                            >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Acknowledge
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Map */}
                <Card className="lg:col-span-2 bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="border-b border-zinc-800 pb-4">
                        <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide flex items-center gap-2">
                            <Navigation className="w-5 h-5 text-orange-500" />
                            Live Fleet Map
                            <Badge variant="outline" className="ml-auto border-zinc-700">
                                {trucksWithLocation.length} trucks tracking
                            </Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="h-[500px] rounded-b-sm overflow-hidden">
                            <MapContainer 
                                center={[39.8283, -98.5795]} 
                                zoom={4} 
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    attribution='&copy; CARTO'
                                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                                />
                                
                                {trucksWithLocation.map((truck) => (
                                    <Marker 
                                        key={truck.truck_id}
                                        position={[truck.current_location.lat, truck.current_location.lng]}
                                        icon={truckIcon}
                                    >
                                        <Popup>
                                            <div className="text-sm">
                                                <strong>{truck.name}</strong><br/>
                                                {truck.driver_name && <span>Driver: {truck.driver_name}<br/></span>}
                                                <span className="text-gray-500">
                                                    Last update: {new Date(truck.last_location_update).toLocaleTimeString()}
                                                </span>
                                            </div>
                                        </Popup>
                                    </Marker>
                                ))}

                                {/* Show load destinations with 5-mile geofence */}
                                {loads.map((load) => {
                                    if (!load.destination_lat) return null;
                                    return (
                                        <Circle
                                            key={load.load_id}
                                            center={[load.destination_lat, load.destination_lng]}
                                            radius={8047}  // 5 miles in meters
                                            pathOptions={{
                                                color: '#22c55e',
                                                fillColor: '#22c55e',
                                                fillOpacity: 0.1
                                            }}
                                        />
                                    );
                                })}
                            </MapContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Trucks List */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="border-b border-zinc-800 pb-4">
                        <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide">
                            Fleet Status
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 max-h-[500px] overflow-y-auto">
                        {trucks.length === 0 ? (
                            <div className="text-center py-12 text-zinc-500">
                                <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>No trucks configured</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-zinc-800">
                                {trucks.map((truck) => {
                                    const assignedLoad = loads.find(l => l.assigned_truck_id === truck.truck_id);
                                    return (
                                        <div 
                                            key={truck.truck_id}
                                            className="p-4 hover:bg-zinc-800/30"
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <div>
                                                    <p className="text-white font-semibold">{truck.name}</p>
                                                    {truck.driver_name && (
                                                        <p className="text-zinc-500 text-sm">{truck.driver_name}</p>
                                                    )}
                                                </div>
                                                <Badge className={truck.current_location 
                                                    ? 'bg-green-500/20 text-green-400' 
                                                    : 'bg-zinc-700 text-zinc-400'
                                                }>
                                                    {truck.current_location ? 'GPS Active' : 'No GPS'}
                                                </Badge>
                                            </div>

                                            {assignedLoad && (
                                                <div className="mb-3 p-2 bg-zinc-800/50 rounded-sm">
                                                    <p className="text-xs text-zinc-500">Active Load</p>
                                                    <p className="text-sm text-orange-400">{assignedLoad.load_id}</p>
                                                    <p className="text-xs text-zinc-400">
                                                        {assignedLoad.origin_city} → {assignedLoad.destination_city}
                                                    </p>
                                                </div>
                                            )}

                                            <div className="flex gap-2">
                                                <Button 
                                                    size="sm" 
                                                    variant="outline"
                                                    className="flex-1 border-zinc-700 text-xs"
                                                    onClick={() => generateTrackingLink(truck)}
                                                >
                                                    <MessageSquare className="w-3 h-3 mr-1" />
                                                    Text-to-Track
                                                </Button>
                                                {assignedLoad && (
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline"
                                                        className="flex-1 border-zinc-700 text-xs"
                                                        onClick={() => createShareLink(assignedLoad)}
                                                    >
                                                        <Share2 className="w-3 h-3 mr-1" />
                                                        Share
                                                    </Button>
                                                )}
                                            </div>

                                            {truck.current_location && (
                                                <p className="text-xs text-zinc-500 mt-2">
                                                    Last ping: {new Date(truck.last_location_update).toLocaleString()}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Text-to-Track SMS Dialog */}
            <Dialog open={showSMSDialog} onOpenChange={setShowSMSDialog}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white">Text-to-Track Link</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Send this link to the driver via SMS
                        </DialogDescription>
                    </DialogHeader>
                    
                    {smsLink && (
                        <div className="space-y-4 py-4">
                            <div className="p-4 bg-zinc-800/50 rounded-sm">
                                <p className="text-xs text-zinc-500 mb-2">DRIVER PHONE</p>
                                <div className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-zinc-400" />
                                    <span className="text-white">
                                        {selectedTruck?.driver_phone || 'Not configured'}
                                    </span>
                                </div>
                            </div>

                            <div className="p-4 bg-zinc-800/50 rounded-sm">
                                <p className="text-xs text-zinc-500 mb-2">TRACKING URL</p>
                                <div className="flex items-center gap-2">
                                    <Input 
                                        value={smsLink.tracking_url}
                                        readOnly
                                        className="bg-zinc-900 border-zinc-700 text-xs"
                                    />
                                    <Button 
                                        size="icon"
                                        variant="outline"
                                        className="border-zinc-700"
                                        onClick={() => copyToClipboard(smsLink.tracking_url)}
                                    >
                                        <Copy className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="p-4 bg-zinc-800/50 rounded-sm">
                                <p className="text-xs text-zinc-500 mb-2">SMS MESSAGE</p>
                                <p className="text-sm text-white">{smsLink.sms_message}</p>
                                <Button 
                                    size="sm"
                                    variant="outline"
                                    className="mt-2 border-zinc-700"
                                    onClick={() => copyToClipboard(smsLink.sms_message)}
                                >
                                    <Copy className="w-4 h-4 mr-2" />
                                    Copy Message
                                </Button>
                            </div>

                            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                                <p className="text-yellow-400 text-xs">
                                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                                    MOCK: In production, this sends via Twilio SMS API
                                </p>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button 
                            onClick={() => setShowSMSDialog(false)}
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                        >
                            Done
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Share Tracking Dialog */}
            <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white">Share Tracking</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Share read-only tracking link with broker
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="p-4 bg-zinc-800/50 rounded-sm">
                            <p className="text-xs text-zinc-500 mb-2">LOAD</p>
                            <p className="text-orange-400">{selectedLoad?.load_id}</p>
                            <p className="text-sm text-zinc-400">
                                {selectedLoad?.origin_city} → {selectedLoad?.destination_city}
                            </p>
                        </div>

                        <div className="p-4 bg-zinc-800/50 rounded-sm">
                            <p className="text-xs text-zinc-500 mb-2">SHARE URL (Read-Only)</p>
                            <div className="flex items-center gap-2">
                                <Input 
                                    value={shareUrl}
                                    readOnly
                                    className="bg-zinc-900 border-zinc-700 text-xs"
                                />
                                <Button 
                                    size="icon"
                                    variant="outline"
                                    className="border-zinc-700"
                                    onClick={() => copyToClipboard(shareUrl)}
                                >
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-sm">
                            <p className="text-green-400 text-xs">
                                <CheckCircle className="w-4 h-4 inline mr-1" />
                                Link auto-expires when POD is uploaded
                            </p>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button 
                            onClick={() => setShowShareDialog(false)}
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                        >
                            Done
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
